import { useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBarsStaggered,
  faFilter,
  faFilterCircleXmark,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import { HeaderButtonBurger } from "../common/header/HeaderButtonBurger";
import { CyItems } from "./CyItems";
import { SearchEngineEntry } from "../search/SearchEngineEntry";
import { SettingsModal } from "../common/components/multiselectablelist/settings/SettingsModal";
import { Column } from "../common/components/multiselectablelist/Column";
import { Link } from "react-router-dom";
import { ItemsFilterModal } from "./filter/ItemsFilterModal";
import { CyItemsHeaderFirstRow } from "./CyItemsHeaderFirstRow";
import { useTranslation } from "react-i18next";

interface Props<E> {
  columns: Column<E>[];
  changeOrderBy: (newField: keyof E) => void;
  showDetails: boolean;
  setShowDetails: (value: ((prevState: boolean) => boolean) | boolean) => void;
  orderByAscOrDesc: "asc" | "desc";
  orderByField: keyof E;
  entryLimit: number;
  setEntryLimit: (value: ((prevState: number) => number) | number) => void;
  setIsMainMenuOpen: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  allTonalities: string[];
  selectedTonalities: string[];
  setSelectedTonalities: (
    value: ((prevState: string[]) => string[]) | string[],
  ) => void;
  allLanguages: string[];
  selectedLanguages: string[];
  setSelectedLanguages: (
    value: ((prevState: string[]) => string[]) | string[],
  ) => void;
  allTags: string[];
  selectedTags: string[];
  setSelectedTags: (
    value: ((prevState: string[]) => string[]) | string[],
  ) => void;
  searchTerm: string | undefined;
  clearFilters: () => void;
}

export function ItemsHeaderFirstRow({
  columns,
  changeOrderBy,
  showDetails,
  setShowDetails,
  orderByAscOrDesc,
  orderByField,
  entryLimit,
  setEntryLimit,
  setIsMainMenuOpen,
  allTonalities,
  selectedTonalities,
  setSelectedTonalities,
  allLanguages,
  selectedLanguages,
  setSelectedLanguages,
  allTags,
  selectedTags,
  setSelectedTags,
  searchTerm,
  clearFilters,
}: Props<SearchEngineEntry>) {
  const { t } = useTranslation();

  const [advancedMenuOpen, setAdvancedMenuOpen] = useState<boolean>(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(false);

  const isFilterActive =
    selectedTonalities.length > 0 ||
    selectedLanguages.length > 0 ||
    selectedTags.length > 0 ||
    (searchTerm !== undefined && searchTerm.length > 0);

  return (
    <>
      <Row className="gy-2 pt-2">
        <Col className="col-auto d-flex order-1">
          <div className="d-flex with-left-column">
            <HeaderButtonBurger setIsMainMenuOpen={setIsMainMenuOpen} />
          </div>
        </Col>
        <Col className="d-flex col-8 col-md order-3 order-md-2 align-items-center ps-1">
          <h3 className="mb-0" data-cy={CyItems.PageHeader}>
            {t("menu.manageSongsheets")}
          </h3>
        </Col>
        <Col className="col-auto order-2 order-md-3 d-flex justify-content-end ">
          <Button
            color="primary"
            className="me-2"
            data-cy={CyItems.PlaylistsButton}
            to={"/"}
            tag={Link}
          >
            <FontAwesomeIcon icon={faBarsStaggered} className="pe-2" />
            {t("menu.playlists")}
          </Button>
          {isFilterActive && (
            <Button color="danger" className="me-2" onClick={clearFilters}>
              <FontAwesomeIcon icon={faFilterCircleXmark} />
            </Button>
          )}
          <Button
            color="primary"
            className="me-2"
            active={filterMenuOpen}
            onClick={() => {
              setFilterMenuOpen((p) => !p);
            }}
            data-cy={CyItemsHeaderFirstRow.FilterButton}
          >
            <FontAwesomeIcon icon={faFilter} />
          </Button>
          <Button
            color="primary"
            active={advancedMenuOpen}
            onClick={() => {
              setAdvancedMenuOpen((p) => !p);
            }}
          >
            <FontAwesomeIcon icon={faGear} />
          </Button>
        </Col>
      </Row>

      <SettingsModal
        columns={columns}
        changeOrderBy={changeOrderBy}
        currentOrder={orderByAscOrDesc}
        currentField={orderByField}
        showDetails={showDetails}
        setShowDetails={setShowDetails}
        entryLimit={entryLimit}
        setEntryLimit={setEntryLimit}
        isModalOpen={advancedMenuOpen}
        setIsModalOpen={setAdvancedMenuOpen}
        limitOptions={[20, 50, 100, 1000]}
      />

      <ItemsFilterModal
        allTonalities={allTonalities}
        selectedTonalities={selectedTonalities}
        setSelectedTonalities={setSelectedTonalities}
        allLanguages={allLanguages}
        selectedLanguages={selectedLanguages}
        setSelectedLanguages={setSelectedLanguages}
        allTags={allTags}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        isModalOpen={filterMenuOpen}
        setIsModalOpen={setFilterMenuOpen}
      />
    </>
  );
}
